import { TFunction } from 'i18next';
import { Recipient, Shipment } from '../../models/parcelDeliveryMiles';
import { getFormattedPrice } from '../../utils/price/get-formatted-price';
import {
  getMeasureUnits,
  Measure,
} from '../../utils/parcel-measurements.ts/parcel-measurements-utils';
import { isFeatureRequired } from '../../utils/client/client.utils';
import { formatDocumentValue } from '../../features/parcel-creation/receiver-details-summary/receiver-details-summary.component';

interface CopyOptions {
  t: TFunction;
  measures: Measure;
  getCurrencySymbol: (currency?: string) => string;
  appDownloadLink?: string;
}

export const getParcelCopyText = (
  shipment: Shipment,
  { t, measures, getCurrencySymbol, appDownloadLink }: CopyOptions
): string => {
  const { sender, recipient, parcel } = shipment;
  const price = shipment.converted_price ?? shipment.price;
  const currencySymbol = getCurrencySymbol(
    price?.currency ?? shipment.currency
  );
  const formatPrice = (value: number) =>
    getFormattedPrice(value, currencySymbol);
  const data = [
    `${t('tracking_number')}: ${shipment.tracking_number || t('no_info')}`,
    `${t('approximate_delivery_date')}: ${
      shipment.status === 'created'
        ? t('parcel_not_sent_yet', { deliveryTime: shipment.delivery_time })
        : shipment.delivery_at || t('no_info')
    }`,
  ];

  const addContact = (subject: Recipient | null, isSender: boolean) => {
    const address = subject?.address;
    const fullAddress = [
      subject?.country || address?.country,
      address?.full_address ||
        [
          address?.post_code,
          address?.region,
          address?.city,
          [address?.building, address?.street].filter(Boolean).join(' '),
          address?.section,
          address?.apartment,
          address?.buzz_code,
        ]
          .filter(Boolean)
          .join(', '),
    ]
      .filter(Boolean)
      .join(', ');
    const name = [subject?.first_name, subject?.middle_name, subject?.last_name]
      .filter(Boolean)
      .join(' ');
    const phone = subject?.phone?.phone_number;
    const contacts = [
      name,
      phone ? `+${phone.replace(/^\+/, '')}` : '',
      subject?.email,
    ]
      .filter(Boolean)
      .join(', ');
    data.push(
      `\n${t(isSender ? 'from' : 'to')}: ${fullAddress || t('no_info')}`
    );
    data.push(
      `${t(isSender ? 'sender' : 'receiver')}: ${contacts || t('no_info')}`
    );

    const documentSummary = subject?.documents_summary;
    if (documentSummary) {
      documentSummary.forEach((x) =>
        data.push(`${x.title}: ${formatDocumentValue(x.value, x.type)}`)
      );
    }

    const carrier = subject?.carrier;
    const delivery = [
      carrier?.delivery_carrier_name,
      carrier?.delivery_service_name || carrier?.delivery_service?.service_type,
    ]
      .filter(Boolean)
      .join(' - ');
    if (delivery) data.push(`${delivery}`);
    if (carrier?.pudo_info?.full_address)
      data.push(`${carrier.pudo_info.full_address}`);
    if (address?.note) data.push(`${address.note}`);
  };
  addContact(sender, true);
  addContact(recipient, false);

  if (parcel) {
    const units = getMeasureUnits(measures);
    const details = [
      parcel.weight != null ? `${parcel.weight} ${t(units.weight)}` : '',
      [parcel.size_z, parcel.size_x, parcel.size_y].every(
        (value) => value != null
      )
        ? `${parcel.size_z} x ${parcel.size_x} x ${parcel.size_y} ${t(units.length)}`
        : '',
    ]
      .filter(Boolean)
      .join(', ');
    data.push(`\n${t('parcel_details')}: ${details || t('no_info')}`);
  }
  if (shipment.items?.length) {
    data.push(`\n${t('parcel_content')}:`);
    shipment.items.forEach((item) => {
      data.push(
        [
          `${item.description_standart || item.description_standart_en} - ${item.number_of_items}`,
          `${t('declared_value')} - ${formatPrice(item.value)}`,
          item.description_free
            ? `${t('parcel_content_description_label')} - ${item.description_free}`
            : '',
        ]
          .filter(Boolean)
          .join(', ')
      );
    });
  }

  const isDomestic = !!sender?.country && sender.country === recipient?.country;
  const useGstHst =
    isDomestic && isFeatureRequired('useGstHstInsteadOfTaxes', sender?.country);

  if (
    !isDomestic ||
    isFeatureRequired('showTaxesOnSummaryDomestic', sender?.country)
  ) {
    let taxesKey = useGstHst ? 'GST_HST' : 'taxes';
    if (sender?.country === 'IT') taxesKey = 'taxes_italy';
    const taxesTitle = t(taxesKey);
    if (shipment.taxes > 0 && sender?.country !== 'IT') {
      data.push(`\n${taxesTitle}:`);
      data.push(
        `${t('create_parcel_summary_taxes_paid_by_sender')} - ${formatPrice(shipment.taxes)}`
      );
    }
    if (!isDomestic) {
      data.push(
        `${t('create_parcel_summary_taxes_paid_by_receiver')} - ${t('create_parcel_summary_taxes_paid_by_receiver_text')}`
      );
    }
  }

  const isUnpaid =
    shipment.flags?.can_pay ||
    shipment.status === 'draft' ||
    shipment.flags?.payment_in_process ||
    (shipment.status === 'created' && !shipment.flags?.show_pay_button);
  data.push(`\n${t('payment_details')}`, t(isUnpaid ? 'unpaid' : 'paid'));
  shipment.price_details?.forEach((detail) => {
    const label =
      detail.type === 'taxes' && useGstHst ? t('GST_HST') : detail.description;
    data.push(`${label} - ${formatPrice(detail.value)}`);
  });
  data.push(
    `${t('total')} - ${price?.total_price != null ? formatPrice(price.total_price) : t('no_info')}`
  );
  if (shipment.markers?.promo) data.push(`\n${shipment.markers.promo}`);
  if (appDownloadLink) data.push(`\n${appDownloadLink}`);
  return data.join('\n');
};
